import React, { useState, useEffect, useMemo } from 'react';
import './SurveyData.css';

// Sanitize CSV cell values to prevent formula injection attacks
// Prefixes cells starting with =, +, -, @, \t, \r with a single quote
const sanitizeCsvCell = (value: unknown): string => {
  const str = String(value ?? '');
  const dangerousChars = ['=', '+', '-', '@', '\t', '\r'];
  if (dangerousChars.some(char => str.startsWith(char))) {
    return "'" + str;
  }
  return str;
};

interface SurveySubmission {
  id: number;
  email: string;
  calendar_preference: number | null;
  created_at: string;
  calendar_rankings: Array<{
    calendar_option: number;
    rank_position: number;
  }> | null;
  questionnaire_answers: Record<string, any> | null;
}

interface QuestionMapping {
  [key: string]: {
    text: string;
    options?: { [key: string]: string };
  };
}

const QUESTIONS_MAP: QuestionMapping = {
  q1: {
    text: 'When would you prefer the school year to begin?',
    options: {
      'early-august': 'Early August (around August 3)',
      'end-august': 'End of August (around August 27)',
      'no-preference': 'No preference'
    }
  },
  q2: {
    text: 'What is most important to you regarding holiday breaks?',
    options: {
      'current-breaks': 'Current breaks during Fall, Thanksgiving, Winter, and Spring',
      'shorter-breaks': 'Shorter breaks during Fall, and Thanksgiving',
      'no-preference': 'No preference'
    }
  },
  q3: {
    text: 'Do you prefer Spring Break to align with Easter?',
    options: {
      'align-easter': 'Yes – Align with Easter',
      'separate': 'No – Keep it separate',
      'no-preference': 'No preference'
    }
  },
  q4: {
    text: 'How important is it to your family that our school calendar aligns closely with nearby districts?',
    options: {
      'very-important': 'Very important – We prefer strong alignment with nearby districts',
      'somewhat-important': 'Somewhat important – Alignment helps but is not a top priority',
      'not-important': 'Not important – We prioritize our own district\'s calendar',
      'no-preference': 'No preference'
    }
  },
  q5: {
    text: 'When choosing a calendar, which factor matters most to your family?',
    options: {
      'shorter-summer': 'Shorter summer, earlier start and longer holiday breaks',
      'longer-summer': 'Longer summer, later start, shorter breaks and later end date',
      'balance': 'Balance of both / no preference'
    }
  },
  q6: {
    text: 'Is there anything else you would like us to consider when deciding on the school calendar?'
  }
};

const ITEMS_PER_PAGE = 20;

const SurveyData: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [submissions, setSubmissions] = useState<SurveySubmission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOption, setFilterOption] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'email'>('newest');
  const [authToken, setAuthToken] = useState('');
  const [authenticating, setAuthenticating] = useState(false);

  // Enable scrolling for data page
  useEffect(() => {
    const originalHtmlStyle = document.documentElement.style.cssText;
    const originalBodyStyle = document.body.style.cssText;

    // Override Survey.css scroll prevention
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';
    document.documentElement.style.position = 'static';
    (document.documentElement.style as any).scrollbarWidth = 'auto';
    (document.documentElement.style as any).msOverflowStyle = 'auto';

    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    document.body.style.position = 'static';
    (document.body.style as any).scrollbarWidth = 'auto';
    (document.body.style as any).msOverflowStyle = 'auto';

    return () => {
      // Restore original styles when component unmounts
      document.documentElement.style.cssText = originalHtmlStyle;
      document.body.style.cssText = originalBodyStyle;
    };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthenticating(true);
    setError('');

    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: 'Invalid password.' }));
        setError(data.error || 'Failed to authenticate.');
        return;
      }

      const { token } = await response.json();
      if (!token) {
        setError('Authentication token missing from response.');
        return;
      }

      setAuthToken(token);
      setIsAuthenticated(true);
      setPassword('');
      await fetchSurveyData(token);
    } catch (err) {
      console.error('Error during admin login:', err);
      setError('Unable to authenticate. Please try again.');
    } finally {
      setAuthenticating(false);
    }
  };

  const fetchSurveyData = async (tokenOverride?: string) => {
    const tokenToUse = tokenOverride || authToken;
    if (!tokenToUse) {
      setError('Missing admin authorization. Please log in again.');
      setIsAuthenticated(false);
      setSubmissions([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/survey-data', {
        headers: {
          Authorization: `Bearer ${tokenToUse}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data);
      } else if (response.status === 401) {
        setIsAuthenticated(false);
        setAuthToken('');
        setSubmissions([]);
        setError('Session expired or unauthorized. Please log in again.');
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching survey data:', error);
      setError('Failed to load survey data. Please make sure the backend server is running.');
      setSubmissions([]);
    }
    setLoading(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getCalendarTitle = (option: number) => {
    const titles = {
      1: '#1 - Early August start, normal Fall break, Spring break separate from Easter, end early June',
      2: '#2 - Late August start, PTC in Nov., shorter Fall & Thanksgiving breaks, Spring break at Easter, end mid-June',
      3: '#3 - Late August start, no Fall break, PTC in Nov., +1 day Winter break, Spring break at Easter, end mid-June'
    };
    return titles[option as keyof typeof titles] || `Option ${option}`;
  };

  const getQuestionText = (questionId: string) => {
    return QUESTIONS_MAP[questionId]?.text || questionId;
  };

  const getAnswerText = (questionId: string, answer: any) => {
    if (!answer) return 'No answer';

    const question = QUESTIONS_MAP[questionId];
    if (question?.options && typeof answer === 'string') {
      return question.options[answer] || answer;
    }

    return String(answer);
  };

  const exportToCSV = () => {
    const headers = ['ID', 'Email', 'Date', 'Calendar Preference', 'Calendar Rankings', ...Object.keys(QUESTIONS_MAP)];

    const rows = submissions.map(submission => {
      const rankings = submission.calendar_rankings
        ?.sort((a, b) => a.rank_position - b.rank_position)
        ?.map(r => `${r.rank_position}. ${getCalendarTitle(r.calendar_option)}`)
        ?.join('; ') || 'No rankings';

      const questionAnswers = Object.keys(QUESTIONS_MAP).map(qId => {
        const answer = submission.questionnaire_answers?.[qId];
        return getAnswerText(qId, answer);
      });

      return [
        submission.id,
        submission.email,
        formatDate(submission.created_at),
        submission.calendar_preference ? getCalendarTitle(submission.calendar_preference) : 'Not specified',
        rankings,
        ...questionAnswers
      ];
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => {
        const sanitized = sanitizeCsvCell(cell);
        return `"${sanitized.replace(/"/g, '""')}"`;
      }).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `survey-data-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Filter and sort submissions
  const filteredAndSortedSubmissions = useMemo(() => {
    let filtered = submissions.filter(submission => {
      const matchesSearch =
        submission.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        submission.id.toString().includes(searchTerm) ||
        (submission.questionnaire_answers &&
         Object.values(submission.questionnaire_answers).some(answer =>
           String(answer).toLowerCase().includes(searchTerm.toLowerCase())
         ));

      if (filterOption === 'all') return matchesSearch;
      if (filterOption === 'no-calendar-pref') return matchesSearch && !submission.calendar_preference;
      if (filterOption === 'has-comments') {
        return matchesSearch && submission.questionnaire_answers?.q6 &&
               String(submission.questionnaire_answers.q6).trim().length > 0;
      }

      return matchesSearch;
    });

    // Sort submissions
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'email':
          return a.email.localeCompare(b.email);
        default:
          return 0;
      }
    });

    return filtered;
  }, [submissions, searchTerm, filterOption, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedSubmissions.length / ITEMS_PER_PAGE);
  const paginatedSubmissions = filteredAndSortedSubmissions.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterOption, sortBy]);

  if (!isAuthenticated) {
    return (
      <div className="survey-data-container">
        <div className="login-section">
          <div className="login-card">
            <div className="login-header">
              <h1>Survey Data Access</h1>
              <p>Enter the admin password to view submission data</p>
            </div>
            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label htmlFor="password">Admin Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <button type="submit" className="login-btn" disabled={authenticating}>
                {authenticating ? 'Checking…' : 'Access Data'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-data-container">
      <div className="data-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Survey Submissions</h1>
            <div className="submission-count">
              Showing {paginatedSubmissions.length} of {filteredAndSortedSubmissions.length} submissions
            </div>
          </div>
          <div className="header-actions">
            <button onClick={exportToCSV} className="export-btn" disabled={submissions.length === 0}>
              <span className="btn-icon">↓</span>
              Export CSV
            </button>
            <button onClick={() => fetchSurveyData()} className="refresh-btn">
              <span className="btn-icon">↻</span>
              Refresh
            </button>
            <button
              onClick={() => {
                setIsAuthenticated(false);
                setAuthToken('');
                setSubmissions([]);
                setError('');
              }}
              className="logout-btn"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {loading && <div className="loading-state">Loading survey data...</div>}
      {error && <div className="error-state">{error}</div>}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-number">{submissions.length}</div>
            <div className="stat-label">Total Submissions</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <div className="stat-number">
              {submissions.length > 0 ? (() => {
                const preferences = submissions.map(s => s.calendar_preference).filter(Boolean);
                if (preferences.length === 0) return 'N/A';

                const counts = preferences.reduce((acc, pref) => {
                  acc[pref!] = (acc[pref!] || 0) + 1;
                  return acc;
                }, {} as Record<number, number>);

                const mostPopular = Object.entries(counts).reduce((a, b) =>
                  counts[parseInt(a[0])] > counts[parseInt(b[0])] ? a : b
                );

                return getCalendarTitle(parseInt(mostPopular[0]));
              })() : 'N/A'}
            </div>
            <div className="stat-label">Most Popular Calendar</div>
          </div>
        </div>
      </div>

      <div className="controls-section">
        <div className="search-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by email, ID, or answer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-controls">
            <select
              value={filterOption}
              onChange={(e) => setFilterOption(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Submissions</option>
              <option value="no-calendar-pref">No Calendar Preference</option>
              <option value="has-comments">With Comments</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="sort-select"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="email">Email A-Z</option>
            </select>
          </div>
        </div>
      </div>

      <div className="submissions-list">
        {paginatedSubmissions.map((submission) => (
          <div key={submission.id} className="submission-card">
            <div className="submission-header">
              <div className="submission-meta">
                <h3 className="submission-id">#{submission.id}</h3>
                <span className="submission-date">{formatDate(submission.created_at)}</span>
              </div>
              <div className="submission-email">{submission.email}</div>
            </div>

            <div className="submission-content">
              <div className="calendar-section">
                <h4>Calendar Preferences</h4>
                <div className="calendar-data">
                  <div className="preference-item">
                    <label>Top Choice:</label>
                    <span className="preference-value">
                      {submission.calendar_preference ?
                        getCalendarTitle(submission.calendar_preference) :
                        'Not specified'
                      }
                    </span>
                  </div>
                  {submission.calendar_rankings && submission.calendar_rankings.length > 0 && (
                    <div className="preference-item">
                      <label>Rankings:</label>
                      <div className="rankings-list">
                        {submission.calendar_rankings
                          .sort((a, b) => a.rank_position - b.rank_position)
                          .map((ranking, index) => (
                            <div
                              key={`${submission.id}-${ranking.calendar_option}-${ranking.rank_position}`}
                              className={`ranking-item rank-${ranking.rank_position}`}
                            >
                              <span className={`rank-badge rank-${ranking.rank_position}`}>
                                {ranking.rank_position}
                              </span>
                              <span className="rank-text">{getCalendarTitle(ranking.calendar_option)}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {submission.questionnaire_answers && (
                <div className="questionnaire-section">
                  <h4>Survey Responses</h4>
                  <div className="answers-list">
                    {Object.entries(submission.questionnaire_answers).map(([questionId, answer]) => {
                      const questionText = getQuestionText(questionId);
                      const answerText = getAnswerText(questionId, answer);

                      return (
                        <div key={questionId} className="answer-item">
                          <div className="question-text">{questionText}</div>
                          <div className={`answer-text ${questionId === 'q6' ? 'textarea-answer' : ''}`}>
                            {answerText}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            First
          </button>
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Last
          </button>
        </div>
      )}

      {filteredAndSortedSubmissions.length === 0 && !loading && (
        <div className="no-data">
          {searchTerm || filterOption !== 'all' ?
            'No submissions match your current filters.' :
            'No survey submissions found.'
          }
        </div>
      )}
    </div>
  );
};

export default SurveyData;
